import {
  CheckCircle,
  RadioButtonUnchecked,
  OndemandVideoRounded,
  KeyboardArrowDown,
} from '@mui/icons-material';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  List,
  useMediaQuery,
} from '@mui/material';
import * as Immutable from 'immutable';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'Reducers';
import { ChapterData, ChapterType } from 'Types/ExerciseData';

export type Timestamp = `${number}:${number}`;

// eslint-disable-next-line react-refresh/only-export-components -- shared util
export function getTime(stamp: Timestamp): number {
  const [min, sec] = stamp.split(':').map(parseFloat);
  return min * 60 + sec;
}

export type ChapterSection = {
  chapters: ChapterData[];
};

type ChapterListProps = {
  sections: ChapterSection[];
  lesson: number;
  active: number;
  jumpToChapter: (x: number, chapter?: number) => void;
  visible: boolean;
};

// Keep in sync with the breakpoint used in Tutorial.tsx / InteractiveTemplate1.tsx
const mobileQuery = '(max-width: 768px), (max-height: 500px)';

const Chapter = ({
  jumpToChapter,
  completionStatus,
  active,
  chapter,
}: {
  jumpToChapter: (x: number, chapter?: number) => void;
  completionStatus: Immutable.Set<number>;
  active: number;
  chapter: ChapterData;
}) => {
  const completed = completionStatus.find((i) => i === chapter.id) !== undefined;
  const icon_color = completed ? '#28856f' : active === chapter.id ? '#ffffff' : '#b2b2b2';
  const timeInSecs = getTime(chapter.start);
  return (
    <ListItemButton
      onClick={() => jumpToChapter(timeInSecs, chapter.id)}
      id={`chapter-${chapter.id}`}
      sx={{
        bgcolor: active === chapter.id ? '#4A5AA1' : 'transparent',
        ':hover': {
          bgcolor: '#4a5aa1',
        },
      }}
      onKeyUp={(e) => {
        if (e.key === ' ') e.preventDefault();
      }}
    >
      {chapter.data[0] === ChapterType.Exercise ? (
        <ListItemIcon>
          {completionStatus.find((i) => i === chapter.id) ? (
            <CheckCircle sx={{ color: icon_color }} />
          ) : (
            <RadioButtonUnchecked sx={{ color: icon_color }} />
          )}
        </ListItemIcon>
      ) : (
        <ListItemIcon>
          <OndemandVideoRounded sx={{ color: icon_color }} />
        </ListItemIcon>
      )}
      <ListItemText sx={{ color: active === chapter.id ? '#ffffff' : '#b2b2b2' }}>
        {chapter.data[0] === ChapterType.Exercise && <b>Exercise: </b>}
        {`${chapter.title} (${formatTime(chapter.start)})`}
      </ListItemText>
    </ListItemButton>
  );
};

function formatTime(timestamp: Timestamp) {
  const [minutes, seconds] = timestamp.split(':');
  const formattedSeconds = Math.floor(parseFloat(seconds)).toString().padStart(2, '0');
  return `${minutes}:${formattedSeconds}`;
}

const ChapterList = ({ sections, lesson, active, jumpToChapter, visible }: ChapterListProps) => {
  // no idea why in these components useSelector returns RootState
  const completionData = useSelector((state: RootState) => state.tutorialReducer);
  const rawCompleted = Immutable.get(completionData.completed, lesson);
  const completionStatus = rawCompleted
    ? Immutable.Set<number>(rawCompleted)
    : Immutable.Set<number>([]);
  let idx = 0;
  const mobileScreen = useMediaQuery(mobileQuery);
  const mobileStyles = {
    position: 'relative',
    height: 'calc(100vh - 60px)',
    top: '60px',
    left: visible ? '0px' : 'calc(-100vw / 3)',
    clipPath: 'inset(0px 10px 10px 10px round 10px)',
    minWidth: 'calc((100vw * 0.334) - 10px)',
    transition: '0.3s ease-in-out',
  };

  // "More to scroll" caret indicator (mobile only) — mirrors the popups in
  // Tempo.tsx: show a down-chevron at the bottom of the panel while the list
  // overflows and the user hasn't scrolled to the end yet.
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const [showScrollMoreHint, setShowScrollMoreHint] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollBodyRef.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    const epsilon = 2;
    const overflow = scrollHeight > clientHeight + epsilon;
    const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
    setShowScrollMoreHint(overflow && !atBottom);
  }, []);

  useLayoutEffect(() => {
    const el = scrollBodyRef.current;
    const inner = scrollContentRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    if (inner) ro.observe(inner);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, mobileScreen]);

  const header = (
    <h3
      style={{
        color: '#b2b2b2',
        fontWeight: mobileScreen ? 'bold' : 'normal',
        margin: mobileScreen ? '10px 30px 0px 30px' : 20,
        paddingBottom: mobileScreen ? '10px' : '0px',
        textAlign: mobileScreen ? 'left' : 'center',
        borderBottom: mobileScreen ? '1px solid #b2b2b255' : 'none',
      }}
    >
      Chapters
    </h3>
  );

  const list = (
    <List sx={{ overflowY: 'scroll', marginTop: mobileScreen ? '-20px' : 'auto' }}>
      {sections.map((section, key1) => (
        <Box key={`section-${key1}`} sx={{ margin: 2, borderBottom: '1px solid #b2b2b255' }}>
          {section.chapters.map((chapter) => (
            <Chapter
              key={`chapter-${idx}`}
              jumpToChapter={jumpToChapter}
              completionStatus={completionStatus}
              active={active}
              chapter={{ ...chapter, id: idx++ }}
            />
          ))}
        </Box>
      ))}
    </List>
  );

  if (mobileScreen) {
    return (
      <Box
        sx={{
          bgcolor: '#1d2f44',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...mobileStyles,
        }}
      >
        <Box
          ref={scrollBodyRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box ref={scrollContentRef}>
            {header}
            {list}
          </Box>
        </Box>
        {showScrollMoreHint && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              // Sit above the panel's 10px bottom clipPath inset so the caret
              // isn't clipped.
              left: 0,
              right: 0,
              bottom: '14px',
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              lineHeight: 0,
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: 22, color: 'rgba(255, 255, 255, 0.52)' }} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#1d2f44',
        overflowY: 'scroll',
      }}
    >
      {header}
      {list}
    </Box>
  );
};

export default ChapterList;
